import type { PathCell, PathLayer } from '../../model/gamestate/GameState';
import {
  PATH_TILE_LAYERS,
  getPathTileLayerDefinition,
  matchesPathTileLayerId,
  normalizePathTileLayerId,
  type PathTileAtlasId,
} from '../pathTileRegistry';
import { isPathTileAtlasId } from '../pathArtStyles';
import { isFixedPathVariantId } from './pathManualVariant';

type PathLayerLike = Pick<PathLayer, 'layerId' | 'atlasId' | 'data'>;
type MutablePathLayer = Omit<PathLayer, 'data'> & { data: (PathCell | null)[] };

export interface CanonicalPathTileEdit {
  readonly atlasId: PathTileAtlasId;
  readonly tileOffset: number;
  readonly cell: PathCell | null;
}

export function canonicalizePathTileLayers(
  width: number,
  height: number,
  layers: readonly PathLayerLike[] | null | undefined,
  gridType?: string,
): PathLayer[] {
  const expectedSize = width * height;
  if (!layers || layers.length === 0 || expectedSize <= 0) {
    return [];
  }

  const canonicalLayers = new Map<PathTileAtlasId, MutablePathLayer>();
  const atlasOrder: PathTileAtlasId[] = [];

  for (const layer of layers) {
    const atlasId = resolveCanonicalAtlasId(layer);
    if (!atlasId) {
      continue;
    }
    let canonicalLayer = canonicalLayers.get(atlasId);
    if (!canonicalLayer) {
      canonicalLayer = {
        layerId: getPathTileLayerDefinition(atlasId)?.layerId ?? normalizePathTileLayerId(atlasId, layer.layerId),
        atlasId,
        width,
        height,
        data: new Array<PathCell | null>(expectedSize).fill(null),
      };
      canonicalLayers.set(atlasId, canonicalLayer);
      atlasOrder.push(atlasId);
    }

    for (let index = 0; index < expectedSize; index += 1) {
      const normalizedCell = normalizePathTileCell(layer.data[index] ?? null, gridType);
      if (!normalizedCell) {
        continue;
      }
      canonicalLayer.data[index] = normalizedCell;
    }
  }

  return atlasOrder
    .map((atlasId) => canonicalLayers.get(atlasId)!)
    .filter((layer) => layer.data.some((cell) => cell !== null));
}

export function applyCanonicalPathTileEdit(
  width: number,
  height: number,
  layers: readonly PathLayer[] | null | undefined,
  edit: CanonicalPathTileEdit,
  gridType?: string,
): PathLayer[] {
  const expectedSize = width * height;
  if (expectedSize <= 0 || edit.tileOffset < 0 || edit.tileOffset >= expectedSize) {
    return canonicalizePathTileLayers(width, height, layers, gridType);
  }

  const nextLayers: MutablePathLayer[] = canonicalizePathTileLayers(width, height, layers, gridType).map((layer) => ({
    ...layer,
    data: [...layer.data],
  }));

  if (edit.cell) {
    const targetLayerId = getPathTileLayerDefinition(edit.atlasId)?.layerId ?? edit.atlasId;
    let targetLayer = nextLayers.find((layer) => layer.atlasId === edit.atlasId || layer.layerId === targetLayerId);
    if (!targetLayer) {
      targetLayer = {
        layerId: targetLayerId,
        atlasId: edit.atlasId,
        width,
        height,
        data: new Array<PathCell | null>(expectedSize).fill(null),
      };
      nextLayers.push(targetLayer);
    }
    targetLayer.layerId = targetLayerId;
    targetLayer.atlasId = edit.atlasId;
    targetLayer.width = width;
    targetLayer.height = height;
    targetLayer.data[edit.tileOffset] = normalizePathTileCell(edit.cell, gridType);
  } else {
    const targetLayerId = getPathTileLayerDefinition(edit.atlasId)?.layerId ?? edit.atlasId;
    const targetLayer = nextLayers.find((layer) => layer.atlasId === edit.atlasId || layer.layerId === targetLayerId);
    if (targetLayer) {
      targetLayer.data[edit.tileOffset] = null;
    }
  }

  return nextLayers.filter((layer) => layer.data.some((cell) => cell !== null));
}

export function resolveCanonicalAtlasId(layer: Pick<PathLayer, 'atlasId' | 'layerId'>): PathTileAtlasId | null {
  if (isPathTileAtlasId(layer.atlasId)) {
    return layer.atlasId;
  }
  for (const atlasId of PATH_TILE_LAYERS.map((entry) => entry.atlasId)) {
    if (matchesPathTileLayerId(atlasId, layer.layerId)) {
      return atlasId;
    }
  }
  return null;
}

function normalizePathTileCell(cell: PathCell | null, gridType?: string): PathCell | null {
  if (!cell || typeof cell !== 'object') {
    return null;
  }
  if (cell.mode === 'autotile') {
    return {
      mode: 'autotile',
      surfaceTerrain:
        cell.surfaceTerrain === 'wall'
        || cell.surfaceTerrain === 'portal'
        || cell.surfaceTerrain === 'path'
        || cell.surfaceTerrain === 'floor'
          ? cell.surfaceTerrain
          : undefined,
    };
  }
  if (cell.mode !== 'fixed' || typeof cell.variantId !== 'string') {
    return null;
  }
  const variantId = cell.variantId.trim();
  const isValidVariant = gridType
    ? isFixedPathVariantId(gridType, variantId)
    : (
      isFixedPathVariantId('square', variantId)
      || isFixedPathVariantId('hex', variantId)
      || isFixedPathVariantId('triangle', variantId)
    );
  if (!variantId || !isValidVariant) {
    return null;
  }
  return {
    mode: 'fixed',
    variantId,
    surfaceTerrain:
      cell.surfaceTerrain === 'wall'
      || cell.surfaceTerrain === 'portal'
      || cell.surfaceTerrain === 'path'
      || cell.surfaceTerrain === 'floor'
        ? cell.surfaceTerrain
        : undefined,
    flipHorizontal: cell.flipHorizontal === true,
    flipVertical: cell.flipVertical === true,
    rotationSteps: Number.isFinite(cell.rotationSteps) ? Math.trunc(cell.rotationSteps) : 0,
  };
}
